import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { callEventPlan, getPlan } from "../../../../../contexts/PlanOperationContext/Plan";
import { AVAILABLE_TIME_TYPE } from "../../../../../util/varibles/constants";
import { addClassName, clearSelect, removeClassName } from "../../../../../util/varibles/global";
import { IPropsChildren, IVessel } from '../../../../../util/varibles/interface';
import { planOpActions } from "../../../reducer";
import { convertPxToTime } from "../../../util";
import { EL_RULE, unavailableBg, VESSEL_COLOR } from "../constants";
import Days from "./Days";
import styles from "./style.module.scss";

let pageX = 0, pageY = 0, scrollLeft = 0, scrollTop = 0;

interface IProps extends IPropsChildren {
    vessel: IVessel
    width: number
    index: number
    calendarId: string
    visible?: boolean
    isAllowDrag?: boolean
    background?: string
    borderLeft?: string
    allowAction?: boolean
}

const Vessel: React.FC<IProps> = (props) => {
    const { vessel, index, visible, isAllowDrag = true, allowAction = true } = props;
    const [dragging, setDragging] = useState(false);
    const { isOwn, permission: vesselPermission, available_time_type } = vessel;
    const { id, isShow } = vessel;
    const get = getPlan();
    const { setTarget } = callEventPlan();
    const dispatch = useDispatch();

    const handleMouseMove = useCallback((e: any) => {
        const { calendarRef } = get();
        const calendar = calendarRef?.();
        if (calendar) {
            calendar.scrollLeft = scrollLeft - e.pageX + pageX;
            calendar.scrollTop = scrollTop - e.pageY + pageY;
        }
    }, [get]);

    const handleMouseUp = useCallback(() => {
        setDragging(false);
        removeClassName(document.body);
        clearSelect();
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove])

    useEffect(() => {
        return handleMouseUp;
    }, [handleMouseUp]);

    const isAccessFull = !!(isOwn || vesselPermission || available_time_type === AVAILABLE_TIME_TYPE.ALL);

    const handleMouseDown = useCallback((e: any) => {
        dispatch(planOpActions.clearFocus());
        const { startCalendar: startPoint, permission } = get();
        const time = startPoint + convertPxToTime(e.nativeEvent.layerY);
        if (allowAction && permission.OP_MENU && e.button === 2)
            setTarget({ type: EL_RULE.VESSEL, vessel, time }, e)

        if (e.button !== 0 || !isAllowDrag)
            return;

        pageX = e.pageX;
        pageY = e.pageY;
        const { calendarRef } = get();
        const calendar = calendarRef?.();
        if (calendar) {
            scrollLeft = calendar.scrollLeft;
            scrollTop = calendar.scrollTop;
        }
        setDragging(true);
        addClassName(document.body);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [allowAction, dispatch, get, setTarget, vessel, isAllowDrag, handleMouseMove, handleMouseUp]);

    const color = VESSEL_COLOR[index % VESSEL_COLOR.length];
    const style: any = isAccessFull ? { background: color } : {
        backgroundAttachment: 'fixed',
        backgroundImage: unavailableBg(color),
    }

    if (props.background) {
        style.background = props.background;
        style.backgroundImage = 'none';
    }
    if (props.borderLeft)
        style.borderLeft = props.borderLeft;

    style.display = (isShow || visible) ? 'grid' : 'none';
    style.minWidth = props.width + '%';

    const handleDayClick = useCallback((time: number, e: any) => {
        const { permission } = get();
        if (allowAction && e.button === 2 && permission.VESSEL_MENU) {
            setTarget({ type: EL_RULE.VESSEL, vessel, time }, e)
        }
    }, [allowAction, get, setTarget, vessel]);

    const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!allowAction) {
            event.preventDefault();
            event.stopPropagation();
        }
    }, [allowAction]);

    return <div
        id={`${EL_RULE.VESSEL}-${id}`}
        className={styles.vessel}
        style={style}
        data-dragging={dragging}
        data-rule={EL_RULE.VESSEL}
        data-vessel-id={id}
        data-access-full={isAccessFull}
        data-allow-drag={isAllowDrag}
        onMouseDown={handleMouseDown}
        onContextMenu={handleContextMenu}
    >
        {props.children}
        <Days
            vesselId={vessel.id}
            isAccessFull={isAccessFull}
            onMouseDown={handleDayClick}
        />
    </div>;
};

export default Vessel
